import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavigationBadge, useNavigationBadgesStore } from "../..";
import {
  addIssuenDescription,
  addQuestionDescription,
  getAllQuestions,
  getTemplateProblems,
} from "../../api/questionsApi";
import ItemsList from "../../components/lists/ItemsList";
import Tabs from "../../components/tabs";
import { Button } from "../../components/ui/button";
import AddItemModal from "../template/addItemModal";
import { AddIcon } from "../studies/AddIcon";
import { BaseDto, QuestionDTO } from "../../types/Rows";

/* ─── Questions tab ──────────────────────────────────────────── */
function QuestionsTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data = [], isFetching } = useQuery(["Questions"], getAllQuestions);

  const { mutate: addQuestion } = useMutation(
    async (name: string) => addQuestionDescription({ value: name }),
    { onSuccess: () => queryClient.invalidateQueries(["Questions"]) }
  );

  const rows: BaseDto[] = data.map((x) => ({
    id: x.id,
    name: x.description,
    description: x.description,
    created: x.created,
    createdBy: x.createdBy,
    edited: x.created,
    EditedBy: x.createdBy,
  }));

  return (
    <ItemsList
      isLoading={isFetching}
      data={rows}
      urlPrefix="/questions/"
      isSortVisible
      isSearchVisible
    >
      <header>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-[15px] font-semibold text-[var(--sc-text)]">
            {t("questions")}
          </h2>
          <AddItemModal text={t("addQuestion")} saveItem={(name) => addQuestion(name)}>
            <AddIcon />
          </AddItemModal>
        </div>
      </header>
    </ItemsList>
  );
}

/* ─── Issues tab ─────────────────────────────────────────────── */
function IssuesTab() {
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<BaseDto[]>([]);

  const { data, isFetching } = useQuery(["Problems"], getTemplateProblems);

  const { mutate: addIssue } = useMutation(
    async (name: string) => addIssuenDescription({ value: name }),
    { onSuccess: () => queryClient.resetQueries(["Problems"]) }
  );

  useEffect(() => {
    if (data) {
      setRows(
        data.categories.map((x) => ({
          id: x.id.toString(),
          name: x.value,
          description: x.value,
        } as BaseDto))
      );
    }
  }, [data]);

  return (
    <ItemsList
      isLoading={isFetching}
      data={rows}
      urlPrefix="/issues/"
      isSortVisible
      isSearchVisible
    >
      <header>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-[15px] font-semibold text-[var(--sc-text)]">
            Znaleziska
          </h2>
          <AddItemModal text="Dodaj znalezisko" saveItem={(name) => addIssue(name)}>
            <AddIcon />
          </AddItemModal>
        </div>
      </header>
    </ItemsList>
  );
}

/* ─── Admin panel page ───────────────────────────────────────── */
export const AdminPanel = () => {
  const setItems = useNavigationBadgesStore((state: any) => state.setItems);

  useEffect(() => {
    setItems([{ text: "Panel administratora", url: "/admin" } as NavigationBadge]);
  }, [setItems]);

  const tabs = [
    { id: "questions", title: "Pytania",    component: <QuestionsTab /> },
    { id: "issues",    title: "Znaleziska", component: <IssuesTab /> },
  ];

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-[20px] font-semibold text-[var(--sc-text)]">Panel administratora</h1>
        <p className="text-[13px] text-[var(--sc-muted)] mt-1">
          Zarządzaj słownikami pytań i znalezisk używanych w raportach.
        </p>
      </div>

      {/* Tabbed content */}
      <div className="rounded-[12px] border border-[var(--sc-border)] bg-[var(--sc-surface)] overflow-hidden">
        <div className="px-5 pt-4">
          <Tabs categories={tabs} />
        </div>
      </div>
    </div>
  );
};
